import { PushNotifications, type Token, type PushNotificationSchema, type ActionPerformed } from '@capacitor/push-notifications'
import { isCapacitorNative } from '../api/ZionServer.ts'
import { PUSH_API_BASE } from '../api/ZionServer.ts'
import { setPushEnabledLocally, savePushSubscriptionJson, clearPushSubscriptionLocal } from '../storage/pushStore.ts'

const NATIVE_TOKEN_KEY = 'zion_native_push_token'
let listenersWired = false

export function isNativePushPlatform(): boolean {
  try {
    if (!isCapacitorNative()) return false
    return true
  } catch {
    return isCapacitorNative()
  }
}

function saveNativeTokenLocal(token: string): void {
  try {
    localStorage.setItem(NATIVE_TOKEN_KEY, token)
  } catch {
    /* ignore */
  }
}

export function loadNativeTokenLocal(): string {
  try {
    return localStorage.getItem(NATIVE_TOKEN_KEY)?.trim() ?? ''
  } catch {
    return ''
  }
}

/** Registra APNs token sul backend (subscription sintetica + campi nativi). */
export async function registerNativePushOnServer(
  sessionToken: string,
  deviceToken: string,
): Promise<boolean> {
  const session = sessionToken.trim()
  const token = deviceToken.trim()
  if (!session || !token) return false
  try {
    const res = await fetch(`${PUSH_API_BASE}/zion/push/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionToken: session,
        platform: 'ios-capacitor',
        deviceToken: token,
        subscription: {
          endpoint: `apns://ios/${encodeURIComponent(token)}`,
          expirationTime: null,
          keys: {
            p256dh: 'native-apns',
            auth: token.slice(0, 22) || 'native',
          },
        },
      }),
    })
    return res.ok
  } catch (err) {
    console.warn('[ZION native push] register failed', err)
    return false
  }
}

function wireNativePushListeners(): void {
  if (listenersWired) return
  listenersWired = true

  void PushNotifications.addListener('registration', (t: Token) => {
    const token = String(t.value ?? '').trim()
    if (!token) return
    console.log('APNs Token obtained:', token)
    console.info('[ZION native push] APNs token', token.slice(0, 12) + '…')
    saveNativeTokenLocal(token)
    savePushSubscriptionJson(
      JSON.stringify({
        endpoint: `apns://ios/${encodeURIComponent(token)}`,
        keys: { p256dh: 'native-apns', auth: token.slice(0, 22) || 'native' },
      }),
    )
    setPushEnabledLocally(true)
    void import('../storage/sessionStore.ts').then(async ({ getSessionToken }) => {
      const session = await getSessionToken()
      if (session) void registerNativePushOnServer(session, token)
    })
  })

  void PushNotifications.addListener('registrationError', (err) => {
    console.warn('[ZION native push] registrationError', err)
  })

  /**
   * Foreground: banner nativo via presentationOptions.
   * Aggiorna contatore unread in-app + badge icona rossa.
   */
  void PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    console.log('Received Push:', notification)
    document.querySelector('#zion-fg-push-banner')?.remove()
    document.querySelector('#zion-inapp-toast-host')?.remove()
    void handleForegroundPushUnread(notification)
    const data = (notification.data ?? {}) as Record<string, unknown>
    const type = String(data.type ?? data.event ?? data.kind ?? '').trim().toLowerCase()
    if (type === 'pair_request' || type.includes('pair')) {
      void presentPairRequestFromPush(data, notification)
    }
  })

  void PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    console.info('[ZION native push] action', action.actionId, action.notification?.data)
    const data = (action.notification?.data ?? {}) as Record<string, unknown>
    const type = String(data.type ?? data.event ?? '').trim().toLowerCase()
    if (type === 'pair_request' || type.includes('pair')) {
      void presentPairRequestFromPush(data, action.notification)
    }
  })
}

/** Unread in-app + badge nativo dopo push (senza toast HTML). */
async function handleForegroundPushUnread(notification: PushNotificationSchema): Promise<void> {
  try {
    const { setBadgeCount, syncAppBadge, calculateTotalUnread } = await import('../storage/unreadStore.ts')
    const data = (notification.data ?? {}) as Record<string, unknown>
    const type = String(data.type ?? data.event ?? data.kind ?? '').trim().toLowerCase()
    if (type === 'pair_request' || type.includes('pair')) {
      syncAppBadge()
      return
    }

    // Foreground: l'unread lo aggiorna già il socket (evita doppio conteggio).
    // Qui allineiamo solo il badge rosso sull'icona.
    const badgeFromPush = Number(notification.badge)
    const total = calculateTotalUnread()
    const next =
      Number.isFinite(badgeFromPush) && badgeFromPush > total
        ? Math.floor(badgeFromPush)
        : total
    await setBadgeCount(next)
  } catch (err) {
    console.warn('[ZION native push] badge/unread', err)
  }
}

async function presentPairRequestFromPush(
  data: Record<string, unknown>,
  notification?: PushNotificationSchema,
): Promise<void> {
  try {
    const nick = String(
      data.nickname ??
        data.myNickname ??
        data.fromNickname ??
        data.name ??
        notification?.title ??
        'Utente',
    ).trim()
    const { getPendingPairingRequest, setPendingPairingRequest } = await import('../storage/pairingStore.ts')
    const { showIncomingPairRequestModal } = await import('../ui/modals.ts')
    const { acceptIncomingPairingRequest } = await import('../app/zionApp.ts')
    const { navigate } = await import('../ui/router.ts')

    showIncomingPairRequestModal({
      nickname: nick,
      onAccept: async () => {
        const pending = await getPendingPairingRequest()
        if (pending) {
          const result = await acceptIncomingPairingRequest(pending)
          if (!result.ok) throw new Error(result.error ?? 'Accettazione fallita')
        } else {
          navigate('home')
        }
      },
      onReject: async () => {
        await setPendingPairingRequest(null)
        navigate('home')
      },
    })
  } catch (err) {
    console.warn('[ZION native push] pair modal', err)
  }
}

export type NativePushSetupResult =
  | { ok: true; token: string }
  | { ok: false; reason: string }

/**
 * Capacitor iOS: APNs via @capacitor/push-notifications (niente Service Worker / Web Push).
 */
export async function enableNativePush(sessionToken: string): Promise<NativePushSetupResult> {
  if (!isNativePushPlatform()) {
    return { ok: false, reason: 'Push nativo disponibile solo su Capacitor iOS.' }
  }
  const session = String(sessionToken ?? '').trim()
  if (!session) {
    return { ok: false, reason: 'Sessione non valida. Sblocca di nuovo l\'app e riprova.' }
  }

  try {
    wireNativePushListeners()

    const perm = await PushNotifications.requestPermissions()
    if (perm.receive !== 'granted') {
      return {
        ok: false,
        reason:
          'Permesso notifiche negato. Abilitalo in Impostazioni iOS → Zion → Notifiche.',
      }
    }

    await PushNotifications.register()

    const token = await waitForNativeToken(8_000)
    if (!token) {
      const cached = loadNativeTokenLocal()
      if (cached) {
        const ok = await registerNativePushOnServer(session, cached)
        if (ok) {
          setPushEnabledLocally(true)
          return { ok: true, token: cached }
        }
      }
      return {
        ok: false,
        reason:
          'Token APNs non ancora pronto. Riprova tra qualche secondo (serve capability Push in Xcode).',
      }
    }

    const ok = await registerNativePushOnServer(session, token)
    if (!ok) {
      return { ok: false, reason: 'Registrazione token sul server fallita.' }
    }
    setPushEnabledLocally(true)
    return { ok: true, token }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    return { ok: false, reason: `Errore push nativo: ${msg}` }
  }
}

function waitForNativeToken(timeoutMs: number): Promise<string> {
  const existing = loadNativeTokenLocal()
  if (existing) return Promise.resolve(existing)

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(loadNativeTokenLocal()), timeoutMs)
    void PushNotifications.addListener('registration', (t: Token) => {
      window.clearTimeout(timer)
      resolve(String(t.value ?? '').trim())
    }).then((handle) => {
      window.setTimeout(() => {
        void handle.remove()
      }, timeoutMs + 500)
    })
  })
}

export async function disableNativePush(): Promise<void> {
  try {
    clearPushSubscriptionLocal()
    try {
      localStorage.removeItem(NATIVE_TOKEN_KEY)
    } catch {
      /* ignore */
    }
    await PushNotifications.removeAllDeliveredNotifications()
  } catch (err) {
    console.warn('[ZION native push] disable', err)
  }
}

/**
 * Bootstrap: listeners PRIMA di register().
 * Banner/suono/badge foreground = capacitor.config PushNotifications.presentationOptions.
 */
export async function initNativePushHooks(): Promise<void> {
  if (!isNativePushPlatform()) return
  try {
    wireNativePushListeners()
    await requestNativePushAtStartup()
  } catch (err) {
    console.warn('[ZION native push] init hooks', err)
  }
}

/**
 * All'avvio iOS/Android nativo: requestPermissions → register APNs.
 * Silenzioso. Mai Notification.requestPermission() WebKit.
 */
export async function requestNativePushAtStartup(): Promise<void> {
  if (!isNativePushPlatform()) return
  try {
    wireNativePushListeners()
    // Sempre requestPermissions + register all'avvio (come da CRITICAL FIX)
    const perm = await PushNotifications.requestPermissions()
    if (perm.receive !== 'granted') {
      console.info('[ZION native push] permission not granted at startup:', perm.receive)
      return
    }
    try {
      await PushNotifications.register()
    } catch (e) {
      console.error('APNs registration error:', e)
      return
    }
    // Badge plugin permissions (icona rossa)
    try {
      const { Badge } = await import('@capawesome/capacitor-badge')
      await Badge.requestPermissions()
      const { syncAppBadge } = await import('../storage/unreadStore.ts')
      syncAppBadge()
    } catch {
      /* badge opzionale */
    }
    const token = await waitForNativeToken(8_000)
    if (!token) return
    const { getSessionToken } = await import('../storage/sessionStore.ts')
    const session = await getSessionToken()
    if (session) {
      const ok = await registerNativePushOnServer(session, token)
      if (ok) setPushEnabledLocally(true)
    }
  } catch (err) {
    console.error('[ZION native push] Push auto-register error:', err)
  }
}
